import styled from "styled-components";
import { SPACING, ANIMATION } from "../../../utils/constants";
import { Popper } from "@mui/material";
import { styled as st } from "@mui/material/styles";
import TextField from "@mui/material/TextField";

export const InputWrapper = styled.div`
  position: relative;
  align-items: center;
  margin: 0px ${SPACING.sm}px 0px ${SPACING.sm}px;
  min-width: 120px;
  width: 100%;
`;

export const StyledTextField = st(TextField)({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "var(--color-gray-600)",
    },
    "&:hover fieldset": {
      borderColor: "var(--color-gray-700)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--color-gray-700)",
    },
    width: "100%",
  },
  "& .Mui-error": {
    "&:hover fieldset": {
      borderColor: "red",
    },
  },
  input: {
    color: "var(--color-select-text)",
  },
  button: {
    color: "var(--color-select-icon)",
    marginRight: "8px",
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "var(--color-gray-700)",
  },

  width: "100%",
  paddingRight: "16px",
});

export const InputDateWrapper = styled.div`
  width: 100%;
`;

// Outlined variant that matches the MUI text fields used across the auth forms:
// same 8px radius and primary-colored hover/focus border (see Auth/styles StyledTextField).
export const DateTextField = styled(TextField)`
  && {
    .MuiOutlinedInput-root {
      border-radius: 8px;

      &:hover .MuiOutlinedInput-notchedOutline {
        border-color: var(--color-primary);
      }

      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: var(--color-primary);
      }
    }
  }
`;

export const InputFieldWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const StyledInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  padding: ${SPACING.sm}px ${SPACING.sm + SPACING.xs}px;
  padding-right: ${(props) =>
    props.readOnly
      ? SPACING.xl + SPACING.sm
      : SPACING.xxl + SPACING.lg}px; /* Space for icons */
  border: 1px solid
    ${(props) =>
      props.$error ? "var(--color-error-border)" : "var(--color-gray-border)"};
  border-radius: 0.5rem;
  font-size: var(--text-base);
  transition: ${ANIMATION.transitions.all};
  background: var(--color-card-background);
  color: var(--color-default-text);
  cursor: ${(props) => (props.readOnly ? "pointer" : "text")};

  &:focus-visible {
    outline: none;
    border-color: ${(props) =>
      props.$error ? "var(--color-error-border)" : "var(--color-primary)"};
    box-shadow: ${(props) =>
      props.$error ? "var(--shadow-active)" : "var(--shadow-focus)"};
  }

  &:disabled {
    color: var(--color-gray-700);
    cursor: revert;
  }

  &::placeholder {
    color: var(--color-text-muted);
    opacity: 0.7;
  }
`;

export const ErrorMessage = styled.span`
  display: block;
  margin-top: ${SPACING.xs}px;
  color: var(--color-error);
  font-size: var(--text-xs);
`;

export const IconWrapper = styled.div<{
  $position: "clear" | "calendar";
  $error: boolean;
}>`
  position: absolute;
  right: ${(props) => (props.$position === "clear" ? "2.25rem" : "0.5rem")};
  top: 0;
  bottom: 0;
  margin: auto;
  height: fit-content;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CalendarPopper = styled(Popper)`
  z-index: 1300;

  /* Style the calendar container */
  .MuiDateCalendar-root {
    background-color: var(--color-background-2);
    color: var(--color-text-default);
    border-radius: 0.75rem;
    box-shadow: var(--shadow-large);
    border: 1px solid var(--color-border);
    padding: ${SPACING.sm}px;
  }

  /* Style the calendar header */
  .MuiPickersCalendarHeader-root {
    color: var(--color-text-default);
    padding-bottom: ${SPACING.sm}px;
    margin-bottom: ${SPACING.xs}px;
  }

  .MuiPickersCalendarHeader-label {
    color: var(--color-text-default);
    font-weight: 600;
    font-size: var(--text-md);
  }

  .MuiDayCalendar-weekDayLabel {
    color: var(--color-text-secondary);
    font-weight: 600;
    font-size: var(--text-base);
  }

  /* Style navigation buttons */
  .MuiIconButton-root {
    color: var(--color-text-default);
    transition: ${ANIMATION.transitions.all};

    &:hover {
      color: var(--color-primary);
      background-color: var(--hover-background);
    }

    &:disabled {
      color: var(--color-text-muted);
      opacity: 0.5;
    }
  }

  /* Style calendar days with enhanced states */
  .MuiPickersDay-root {
    color: var(--color-text-default);
    background-color: transparent;
    font-size: var(--text-base);
    font-weight: 500;
    border-radius: 6px;
    transition: ${ANIMATION.transitions.all};

    &:hover {
      background-color: var(--color-calendar-hover-background);
      transform: scale(1.05);
    }

    &:disabled {
      color: var(--color-text-muted);
      background: var(--color-calendar-disabled-background);
      opacity: 0.4;
      text-decoration: line-through;
    }

    &.Mui-selected {
      background: var(--color-primary);
      color: var(--color-white);
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 138, 255, 0.4);

      &:hover {
        background: var(--color-primary-hover);
        transform: scale(1.05);
      }

      &:focus {
        background: var(--color-primary);
      }
    }

    /* Today's date */
    &.MuiPickersDay-today {
      border: 2px solid var(--color-primary);
      font-weight: 600;

      &:not(.Mui-selected) {
        background: transparent;
      }
    }
  }

  /* Style the paper/container */
  .MuiPaper-root {
    background-color: var(--color-background-2);
    border-radius: 0.75rem;
  }

  /* Dark theme specific optimizations */
  [data-theme="dark"] & {
    .MuiPickersDay-root {
      &.Mui-selected {
        box-shadow: 0 2px 12px rgba(124, 138, 255, 0.5);
      }
    }

    .MuiDayCalendar-weekDayLabel {
      color: var(--color-text-secondary);
      opacity: 0.9;
    }
  }
`;
